import React, { useState, useEffect } from "react";
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Line,
  ComposedChart,
} from "recharts";
import FilterSelect from "../../components/select/FilterSelect";
import LoaderComponent from "../../components/loaderCompent/LoaderComponent";

const MonthlyFloatingBarChart = ({
  meterId,
  orgID,
  startDate,
  getFirstMonth,
  monthArray,
}) => {
  const [selectedMonth, setSelectedMonth] = useState(monthArray[0]);
  const [loading, setLoading] = useState(true);
  const [pxData, setPxData] = useState(null);
  const [savingsData, setSavingsData] = useState([]);
  const [chartData, setChartData] = useState([]);

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const monthMap = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };

  const getMonthIndex = (month) => {
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const monthIndex = monthNames.indexOf(month); // Get month index (0-based)

    if (monthIndex === -1) {
      console.error("Invalid month:", month);
      return null; // Return null for invalid month
    }

    return monthIndex;
  };

  const currentYear = startDate.split("/")[1];
  const daysInSelectedMonth = getDaysInMonth(
    monthMap[selectedMonth],
    currentYear
  );

  // Get the first day of the month
  const getFirstDayOfMonth = (month, year) => {
    const monthIndex = getMonthIndex(month);
    if (monthIndex === null) return null;
    return new Date(Date.UTC(year, monthIndex, 1, 0, 0, 0));
  };

  const fetchPowerConsumptionData = async (month) => {
    try {
      setLoading(true); // Start loader

      const firstDayOfMonth = getFirstDayOfMonth(month, currentYear);
      const formattedDate = `${(firstDayOfMonth.getUTCMonth() + 1)
        .toString()
        .padStart(2, "0")}/${firstDayOfMonth.getUTCFullYear()}`;

      const payload = {
        startDate: formattedDate,
        endDate: formattedDate,
        organisationId: orgID,
        stateCode: "IN-MH",
        meterId: meterId,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/recommendation/px-rec?pricetype=constantprice&&returnSpecificFieldsFlag=false`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();
      setPxData(data.data[0]);
      setLoading(false); // End loader
    } catch (error) {
      console.error("Error fetching power consumption data:", error);
    } finally {
      setLoading(false); // End loader
    }
  };

  useEffect(() => {
    fetchPowerConsumptionData(selectedMonth);
  }, []);

  useEffect(() => {
    if (!pxData?.calculations_steps?.savingsFromExchangeTransactions) return;
    const transactions =
      pxData.calculations_steps.savingsFromExchangeTransactions;

    // Transform data: rows should be keys like "totalHours", and columns should be months
    const formattedData = {
      totalHours: {},
      totalCharge: {},
      totalPxChargeMonth: {},
      maxWindow: {},
      dailyData: {},
      formattedMonth: {},
    };

    let months = [];
    Object.entries(transactions).forEach(([month, details], index) => {
      const [startMonth, startYear] = startDate.split("/"); // Split the date string to extract month and year
      let currentYear = parseInt(startYear, 10); // Convert year to number

      if (
        index > 0 &&
        new Date(`${month}-01`).getMonth() <
          new Date(`${Object.keys(transactions)[index - 1]}-01`).getMonth()
      ) {
        currentYear++;
      }
      const formattedMonth = `${month}-${currentYear}`;
      months.push(month);
      formattedData.totalHours[month] = details.totalHours || 0;
      formattedData.totalCharge[month] = details.totalCharge || 0;
      formattedData.totalPxChargeMonth[month] = details.totalPxChargeMonth || 0;
      formattedData.maxWindow[month] = details.maxWindow || 0;
      formattedData.formattedMonth[month] = formattedMonth;

      // Ensure dailyData[month] is initialized as an object
      if (!formattedData.dailyData[month]) {
        formattedData.dailyData[month] = {};
      }

      formattedData.dailyData[month]["data"] = details.data || {};
    });
    setSavingsData(formattedData.dailyData);
  }, [pxData]);

  useEffect(() => {
    if (!savingsData || !selectedMonth || !savingsData[selectedMonth]?.data)
      return;

    const labels = Array.from({ length: daysInSelectedMonth }, (_, i) => i + 1);
    const data = [];

    labels.forEach((day) => {
      const entry = savingsData[selectedMonth]?.data.find((d) => d.day === day);
      let total = 0;

      if (entry && Array.isArray(entry.maxslot)) {
        entry.maxslot.forEach((slot) => {
          total += Math.round(slot?.totalPxCost) ?? 0;
        });

        entry.maxslot.forEach((slot, index) => {
          data.push({
            name: day,
            base: [0, 24],
            shaded: [
              parseInt(slot.start.split(":")[0], 10),
              slot.end === "00:00" ? 24 : parseInt(slot.end.split(":")[0], 10),
            ],
            totalPxCost: Math.round(slot?.totalPxCost) ?? 0,
            totalCharge: slot?.totalDifference ?? 0,
            total: total ?? 0,
            slotIndex: index + 1,
            duration: slot.duration,
            hours: [slot.start, slot.end],
          });
        });
      } else {
        data.push({
          name: day,
          base: [0, 24],
          shaded: [0, 0],
          totalPxCost: 0,
          totalCharge: 0,
          total: 0,
        });
      }
    });

    setChartData(data);
  }, [savingsData, selectedMonth, daysInSelectedMonth]);

  // Handle month change
  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setSelectedMonth(newMonth);
    fetchPowerConsumptionData(newMonth);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const { hours, totalCharge, totalPxCost, duration, total } =
        payload[0].payload;
      return (
        <div className="bg-white border border-gray-300 p-2 shadow-md rounded">
          <p className="text-sm font-semibold text-[green]">Day: {label}</p>
          <p className="text-sm">
            Max trading window time: {hours[0]} - {hours[1]}
          </p>
          <p className="text-sm">
            Total savings (in Rs.) for slot: ₹{totalPxCost}{" "}
          </p>
          <p className="text-sm">Duration: {duration} hrs </p>
          <p className="text-sm">
            Per unit savings: {(totalCharge / (duration * 4)).toFixed(2)}{" "}
            Rs/unit
          </p>
          <p className="text-sm">Total savings (in Rs.) for day: ₹{total} </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="shadow-[0_4px_4px_0_#00000040] w-full relative p-4 border border-[#00000040] rounded-xl sm:min-h-[100px] p-5">
      {!loading ? (
        <>
          <div className="sm:absolute flex justify-start left-0 ml-[4%] sm:left-[3%] items-center gap-4">
            <div className="min-w-[110px]">Select Month: </div>
            <div className="lg:mt-0 sm:w-full mr-6 lg:mr-4">
              <FilterSelect
                label={"Month"}
                height={"30px"}
                onChange={handleMonthChange}
                value={selectedMonth}
                options={monthArray?.map((month) => ({
                  label: month,
                  value: month,
                }))}
              />
            </div>
          </div>

          <div className="flex-grow w-full sm:pl-[2%] sm:pr-[2%] sm:mb-[100px] py-1 h-[45vh] sm:flex justify-center mt-10">
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  type="number"
                  domain={[0, daysInSelectedMonth]}
                  tickCount={daysInSelectedMonth + 1}
                />
                <YAxis
                  yAxisId="left"
                  type="number"
                  orientation="left"
                  domain={[0, 24]}
                  tick={{ fontSize: 15 }}
                  label={{
                    value: "Hours",
                    angle: -90,
                    offset: 10,
                    position: "insideLeft",
                  }}
                  tickCount={25}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  type="number"
                  tick={{ fontSize: 12 }}
                  label={{
                    value: "Savings (₹)",
                    angle: -90,
                    position: "insideRight",
                    offset: 10,
                    dy: -80,
                  }}
                />

                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ margin: "-1rem" }} />

                {/* Shaded Slot Bar */}
                <Bar
                  yAxisId="left"
                  dataKey="shaded"
                  fill="green"
                  radius={[10, 10, 10, 10]}
                  barSize={12}
                  name="Max trading window for day"
                />

                {/* Line Chart for Total PX Cost */}
                <Line
                  yAxisId="right"
                  dataKey="total"
                  strokeWidth={2}
                  dot={{ r: 1 }}
                  name="Total savings (in Rs.)"
                  isAnimationActive={true}
                  type="monotone" // Add this line to make the line curve
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : (
        <LoaderComponent />
      )}
    </div>
  );
};

export default MonthlyFloatingBarChart;
